import React, { useEffect, useMemo, useState } from "react";
import { Camera, CheckCircle2, FileImage, Image as ImageIcon, LockKeyhole, Search, Sparkles, UsersRound } from "lucide-react";

import { apiFetch } from "../../../utils/api";
import "./ContentMediaPage.css";

type ContentMediaAction = {
  label: string;
  target: string;
  entity_id?: string;
};

type ContentMediaAsset = {
  asset_key: string;
  lifecycle_bucket: "course" | "dive_site" | "staff" | "summon_dream" | "witness_story" | string;
  entity_type: string;
  entity_id: string;
  title: string;
  subtitle?: string | null;
  media_url?: string | null;
  thumbnail_url?: string | null;
  media_status: "present" | "missing" | string;
  visibility: string;
  rights_status: string;
  source: string;
  updated_at?: string | null;
  action?: ContentMediaAction;
};

type ContentMediaResponse = {
  ok: boolean;
  status?: string;
  message?: string;
  operator_id?: string;
  summary?: {
    total?: number;
    present?: number;
    missing?: number;
    by_bucket?: Record<string, number>;
    reserved_buckets?: Record<string, string>;
  };
  assets?: ContentMediaAsset[];
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "summon_dream", label: "Trip Inspiration" },
  { key: "course", label: "Courses" },
  { key: "dive_site", label: "Dive Sites" },
  { key: "staff", label: "Team" },
  { key: "witness_story", label: "Post-Dive Memories" },
];

function bucketLabel(bucket: string) {
  if (bucket === "summon_dream") return "Trip Inspiration";
  if (bucket === "witness_story") return "Post-Dive Memories";
  if (bucket === "dive_site") return "Dive Site";
  if (bucket === "course") return "Course";
  if (bucket === "staff") return "Team";
  return bucket;
}

function statusLabel(status: string) {
  if (status === "present") return "Image ready";
  if (status === "missing") return "Needs image";
  return status;
}

function bucketIcon(bucket: string) {
  if (bucket === "summon_dream") return Sparkles;
  if (bucket === "witness_story") return LockKeyhole;
  if (bucket === "staff") return UsersRound;
  if (bucket === "course") return FileImage;
  return Camera;
}

export default function ContentMediaPage() {
  const [assets, setAssets] = useState<ContentMediaAsset[]>([]);
  const [summary, setSummary] = useState<ContentMediaResponse["summary"]>({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.classList.add("cm-route-active");
    return () => {
      document.body.classList.remove("cm-route-active");
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await apiFetch("/api/v1/dashboard/content-media/assets", { method: "GET" });
        const json = (await res.json()) as ContentMediaResponse;

        if (!res.ok || !json.ok) {
          throw new Error(json.message || `Content media HTTP ${res.status}`);
        }

        if (mounted) {
          setAssets(Array.isArray(json.assets) ? json.assets : []);
          setSummary(json.summary || {});
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || "Failed to load Content & Media inventory.");
          setAssets([]);
          setSummary({});
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void load();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredAssets = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return assets.filter((asset) => {
      const matchesFilter = activeFilter === "all" || asset.lifecycle_bucket === activeFilter;
      const matchesQuery =
        !needle ||
        asset.title.toLowerCase().includes(needle) ||
        String(asset.subtitle || "").toLowerCase().includes(needle) ||
        bucketLabel(asset.lifecycle_bucket).toLowerCase().includes(needle);

      return matchesFilter && matchesQuery;
    });
  }, [assets, activeFilter, query]);

  const reservedRows = [
    {
      key: "summon_dream",
      title: "Trip Inspiration",
      subtitle: "Pre-trip imagery for concierge, itinerary, and booking-confidence surfaces.",
      status: summary?.reserved_buckets?.summon_dream || "planned",
    },
    {
      key: "witness_story",
      title: "Post-Dive Memories",
      subtitle: "Private-first post-dive media for guest memories, logbook records, consent, and future photo sales.",
      status: summary?.reserved_buckets?.witness_story || "planned_private",
    },
  ];

  return (
    <div className="cm-page">
      <section className="cm-hero">
        <div>
          <p className="cm-kicker">Adventure media layer</p>
          <h1>Content &amp; Media</h1>
          <p className="cm-intro">
            Manage the structured images and story assets attached to AQUORIX entities. This is not a CMS:
            content stays connected to courses, dive sites, team profiles, itineraries, and post-dive story records.
          </p>
        </div>
      </section>

      <section className="cm-stat-strip" aria-label="Content media summary">
        <div className="cm-stat">
          <span>{summary?.total ?? 0}</span>
          <p>Total records</p>
        </div>
        <div className="cm-stat">
          <span>{summary?.present ?? 0}</span>
          <p>Ready</p>
        </div>
        <div className="cm-stat">
          <span>{summary?.missing ?? 0}</span>
          <p>Needs media</p>
        </div>
        <div className="cm-stat">
          <span>{summary?.by_bucket?.course ?? 0}</span>
          <p>Courses</p>
        </div>
        <div className="cm-stat">
          <span>{summary?.by_bucket?.dive_site ?? 0}</span>
          <p>Dive sites</p>
        </div>
        <div className="cm-stat">
          <span>{summary?.by_bucket?.staff ?? 0}</span>
          <p>Team</p>
        </div>
      </section>

      <section className="cm-reserved">
        {reservedRows.map((row) => {
          const Icon = bucketIcon(row.key);
          return (
            <article className="cm-reserved-item" key={row.key}>
              <div className="cm-reserved-icon">
                <Icon aria-hidden="true" />
              </div>
              <div>
                <h2>{row.title}</h2>
                <p>{row.subtitle}</p>
              </div>
              <span className="cm-reserved-status">{row.status.replace(/_/g, " ")}</span>
            </article>
          );
        })}
      </section>

      <section className="cm-toolbar">
        <div className="cm-tabs" role="tablist" aria-label="Content media filters">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={activeFilter === filter.key ? "is-active" : ""}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <label className="cm-search">
          <Search aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search media records"
          />
        </label>
      </section>

      {error ? <div className="cm-error">{error}</div> : null}

      <section className="cm-inventory">
        <div className="cm-inventory-head">
          <span>Asset</span>
          <span>Bucket</span>
          <span>Status</span>
          <span>Visibility</span>
          <span>Rights</span>
          <span>Action</span>
        </div>

        {loading ? (
          <div className="cm-empty">Loading content media inventory...</div>
        ) : filteredAssets.length ? (
          filteredAssets.map((asset) => {
            const Icon = bucketIcon(asset.lifecycle_bucket);
            const thumb = asset.thumbnail_url || asset.media_url || "";
            return (
              <article className="cm-row" key={asset.asset_key}>
                <div className="cm-asset-cell">
                  <div className="cm-thumb">
                    {thumb ? <img src={thumb} alt="" /> : <ImageIcon aria-hidden="true" />}
                  </div>
                  <div>
                    <h3>{asset.title}</h3>
                    <p>{asset.subtitle}</p>
                    <small>{asset.source}</small>
                  </div>
                </div>

                <div className="cm-bucket-cell">
                  <Icon aria-hidden="true" />
                  {bucketLabel(asset.lifecycle_bucket)}
                </div>

                <span className={`cm-pill ${asset.media_status === "present" ? "is-ready" : "is-missing"}`}>
                  {asset.media_status === "present" ? <CheckCircle2 aria-hidden="true" /> : null}
                  {statusLabel(asset.media_status)}
                </span>

                <span className="cm-plain">{asset.visibility.replace(/_/g, " ")}</span>
                <span className="cm-plain">{asset.rights_status.replace(/_/g, " ")}</span>

                <a className="cm-action" href={asset.action?.target || "/dashboard/content-media"}>
                  {asset.action?.label || "Manage"}
                </a>
              </article>
            );
          })
        ) : (
          <div className="cm-empty">No content media records match this view.</div>
        )}
      </section>
    </div>
  );
}
